import React, { useEffect, useState, useRef } from "react";
import {
  TextField,
  IconButton,
  Typography,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogActions,
  Drawer,
  AppBar,
  Toolbar,
  useTheme,
  Skeleton,
} from "@mui/material";
import { Add, Delete, Close } from "@mui/icons-material";
import WorldMap from "../../assets/baseVersion/Frame.svg";
import RegionManager from "./RegionManager";
import axiosInstance from "../../axios/axios";
import { toast } from "react-toastify";
import { SkeletonLoader } from "../../modules/dashboard/shared";
import Region_UPArrow_img from "../../assets/baseVersion/Region_UPArrow_img.svg";
import useRolePermissions from "../../shared/hooks/useRolePermissions";
import { REGION_ROUTE } from "../../shared/constants/constant-data";

const RegionManagement: React.FC = () => {
  const [levels, setLevels] = useState<any[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [levelToDelete, setLevelToDelete] = useState<any>(null);
  const [showRegionManager, setShowRegionManager] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [inputValues, setInputValues] = useState<Record<number, string>>({});
  const [preloadedRegionData, setPreloadedRegionData] = useState<Array<any>>(
    []
  );
  const [loading, setLoading] = useState(true);
  const [savedValues, setSavedValues] = useState<Record<number, string>>({});
  const [imageLoaded, setImageLoaded] = useState(false);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const { hasPermissions } = useRolePermissions();

  // if (loading) {
  //   return (
  //     <Box p={3}>
  //       <SkeletonLoader count={8} styles={{ height: 30 }} />
  //     </Box>
  //   );
  // }

  //const [preloadedRegionData, setPreloadedRegionData] = useState<Array<Array<{ value: string; editable: boolean }>>>([]);

  useEffect(() => {
    // const fetchRegionLabels = async () => {
    //   try {
    //     const response = await axiosInstance.get('authentication/list-region-labels');
    //     if (response.status === 200) {
    //       if(response.data.data && response.data.data.length === 0) {
    //         setDrawerOpen(true);

    //       } else {
    //         const sortedLevels = response.data.data

    //         .sort((a: any, b: any) => a.level - b.level)
    //         .map((level: any) => ({ id: level.id, name: level.name, level: level.level }));
    //         setLevels(sortedLevels);
    //         setDrawerOpen(false);

    //       }

    //     }
    //   } catch (error) {
    //     console.error('Error fetching region labels:', error);
    //   }
    // };

    fetchRegionLabels();
  }, []);

  useEffect(() => {
    // When levels are loaded or updated, sync savedValues
    const newSavedValues: Record<number, string> = {};
    levels.forEach((level) => {
      newSavedValues[level.level] = level.name || "";
    });
    setSavedValues(newSavedValues);
  }, [levels]);

  useEffect(() => {
    // Reset image loaded state when showing empty state
    if (levels.length === 0 && !loading) {
      setImageLoaded(false);
      // Check if image is already cached and loaded
      if (imageRef.current?.complete && imageRef.current?.naturalHeight !== 0) {
        setImageLoaded(true);
      }
    }
  }, [levels.length, loading]);

  const fetchRegionLabels = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(
        "authentication/list-region-labels"
      );

      if (response.status === 200 && response.data.data.length > 0) {
        const sortedLevels: any = response.data.data.sort(
          (a: any, b: any) => a.level - b.level
        );
        setLevels(sortedLevels);

        let parentId = 0;

        const fullRegionMatrix: Array<Array<any>> = [];

        for (let i = 0; i < sortedLevels.length; i++) {
          const currentId = sortedLevels[i].id;
          sortedLevels[i].parent_id = parentId; // Set parent_id for the current level
          const regionDataResponse = await axiosInstance.get(
            `authentication/list-region-data/${currentId}/${parentId}`
          );
          const regionData = regionDataResponse.data.data;

          console.log(`Level ${i + 1} data:`, regionData);

          if (!regionData || regionData.length === 0) {
            console.log(`No more data found for level ${i + 1}. Stopping.`);
            break;
          }

          // Push the entire column (all regions for this level)
          fullRegionMatrix.push(
            regionData.map((region: any) => ({
              ...region,
              editable: false,
              region_level_id: currentId,
              region_level_name: parentId,
            }))
          );

          // Prepare next parentId — still assuming hierarchy chain logic
          parentId = regionData[0].id;
        }

        setDrawerOpen(false);
        setShowRegionManager(true);
        setPreloadedRegionData(fullRegionMatrix);
        setLoading(false);
      } else {
        setDrawerOpen(true);
      }
    } catch (error) {
      console.error("Error fetching region labels or region data:", error);
      toast.error("Failed to fetch region data.");
    } finally {
      setLoading(false);
      setShowRegionManager(false);
    }
  };

  const handleAddLevel = () => {
    if (levels.length < 5) {
      setLevels([...levels, { level: levels.length + 1, name: "" }]);
    }
  };

  const handleRemoveLevel = (index: number) => {
    const updated = [...levels];
    updated.splice(index, 1);
    const renumberedLevels = updated.map((level, idx) => ({
      ...level,
      level: idx + 1,
    }));
    setLevels(renumberedLevels);
  };

  // Validate level name: alphanumeric and spaces only, min 2, max 40 chars
  const isValidLevelName = (value: string): boolean => {
    const trimmed = value?.trim() || '';
    const alphanumericRegex = /^[a-zA-Z0-9 ]+$/;
    return trimmed.length >= 2 && trimmed.length <= 40 && alphanumericRegex.test(trimmed);
  };

  // Filter level name input: alphanumeric and spaces only, max 40 chars
  const filterLevelNameInput = (value: string): string => {
    return value
      .replace(/[^a-zA-Z0-9 ]/g, '') // Remove non-alphanumeric except spaces
      .slice(0, 40); // Enforce max 40 characters
  };

  const handleChange = (index: number, value: string) => {
    const filteredValue = filterLevelNameInput(value);
    const updated = [...levels];
    updated[index].name = filteredValue;
    setLevels(updated);
  };

  const handleSingleLevelSave = async (level: any) => {
    const payload = level.id
      ? {
          action: "modify",
          region_labels: { id: level.id, name: level.name },
        }
      : {
          action: "add",
          region_labels: [{ name: level.name, level: level.level }],
        };

    try {
      const response = await axiosInstance.post(
        "authentication/region-labels",
        payload
      );
      if (response.data.status === 200) {
        if (response.data.data?.length > 0) {
          const updatedLevel = response.data.data[0];
          setLevels(response.data.data);
        } else {
          setLevels((prevLevels) =>
            prevLevels.map((l) =>
              l.id === level.id
                ? { ...l, name: level.name, level: level.level }
                : l
            )
          );
        }
        toast.success(
          `Level ${level.level} ${level.id ? "updated" : "added"} successfully!`
        );
        setSavedValues((prev) => ({
          ...prev,
          [level.level]: inputValues[level.level]
        }));
      } else {
        toast.error("Something went wrong");
      }
    } catch (error: any) {
      console.error("API Error:", error);

      toast.error(
        error.response?.data?.message ||
          "Failed to save the level. Please try again."
      );
    }
  };

  const handleSave = async () => {
    const addRegionLabels = levels
      .filter((level) => !level.id)
      .map((level) => ({ name: level.name, level: level.level }));

    const modifyRegionLabels = levels
      .filter((level) => level.id)
      .map((level) => ({ id: level.id, name: level.name, level: level.level }));

    const addRequestPayload = { action: "add", region_labels: addRegionLabels };
    const modifyRequestPayload = {
      action: "modify",
      region_labels: modifyRegionLabels,
    };

    try {
      if (addRegionLabels.length > 0) {
        const addResponse = await axiosInstance.post(
          "authentication/region-labels",
          addRequestPayload
        );
        if (addResponse.data.status === "success") {
          console.log("Levels added successfully.");
        }
      }
      if (modifyRegionLabels.length > 0) {
        const modifyResponse = await axiosInstance.post(
          "authentication/region-labels",
          modifyRequestPayload
        );
        if (modifyResponse.data.status === "success") {
          console.log("Levels modified successfully.");
        }
      }
      setShowRegionManager(true);
    } catch (error) {
      console.error("API Call Error:", error);
      alert("Failed to save region labels. Please try again.");
    }
  };

  const confirmDelete = (level: any) => {
    setLevelToDelete(level);
    setOpenDialog(true);
  };

  const handleConfirmDelete = async () => {
    if (levelToDelete && levelToDelete.id) {
      try {
        const requestPayload = {
          action: "delete",
          region_labels: { id: levelToDelete.id },
        };
        const response = await axiosInstance.post(
          "authentication/region-labels",
          requestPayload
        );
        if (response.data.status === 200) {
          const updatedLevels = levels.filter(
            (templevel) => templevel.level !== levelToDelete.level
          );
          const renumberedLevels = updatedLevels.map((level, index) => ({
            ...level,
            level: index + 1,
          }));
          setLevels(renumberedLevels);

          // Reset input value for the deleted level
          setInputValues((prev) => {
            const updated = { ...prev };
            delete updated[levelToDelete.level];
            return updated;
          });
          // Reset saved value for the deleted level
          setSavedValues((prev) => {
            const updated = { ...prev };
            delete updated[levelToDelete.level];
            return updated;
          });

          setOpenDialog(false);
          setLevelToDelete(null);
          toast.success(response.data.message);

          if (renumberedLevels.length === 0) {
            setInputValues({});
            setSavedValues({});
          }
        }
      } catch (error: any) {
        console.error("Delete API Call Error:", error);
        setOpenDialog(false);
        setLevelToDelete(null);
        toast.error(
          error.response?.data?.message ||
            "Failed to delete the level. Please try again."
        );
      }
    } else if (levelToDelete != null) {
      const updatedLevels = levels.filter(
        (templevel) => templevel.level !== levelToDelete.level
      );
      const renumberedLevels = updatedLevels.map((level, index) => ({
        ...level,
        level: index + 1,
      }));
      setLevels(renumberedLevels);

      // Reset input value for the deleted level
      setInputValues((prev) => {
        const updated = { ...prev };
        delete updated[levelToDelete.level];
        return updated;
      });
      // Reset saved value for the deleted level
      setSavedValues((prev) => {
        const updated = { ...prev };
        delete updated[levelToDelete.level];
        return updated;
      });

      setOpenDialog(false);
      setLevelToDelete(null);

      if (renumberedLevels.length === 0) {
        setInputValues({});
        setSavedValues({});
      }
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setLevelToDelete(null);
  };

  const toggleDrawer = () => {
    if (drawerOpen) {
      fetchRegionLabels();
    }
    setDrawerOpen(!drawerOpen);
  };

  if (showRegionManager) {
    if (!loading && levels.length > 0) {
      return (
        <RegionManager
          levels={levels}
          onConfigureBack={() => setShowRegionManager(false)}
          preloadedRegionData={preloadedRegionData}
          isDisabled={drawerOpen}
        />
      );
    }
    return null;
  }

   const theme = useTheme();
    const isDarkMode = theme.palette.mode === "dark";

  // Compute if any Save button is enabled (dirty and valid)
  const isAnySaveEnabled = levels.some(
    (level) =>
      inputValues[level.level] &&
      isValidLevelName(inputValues[level.level]) &&
      inputValues[level.level] !== savedValues[level.level]
  );

  return (
    <>
      {loading ? (
                <SkeletonLoader count={20} styles={{ height: 30 }} />

      ) : (
         <Box display="flex">
          {/* Content shown when no levels */}
          {levels.length === 0 ? (
            <Box
              flex={1}
              display="flex"
              flexDirection="column"
              justifyContent="center"
              // alignItems="center"
              ml={9}
              overflow={"hidden"}
              mt={9}
            >
              {!imageLoaded && (
                <Box sx={{ width: "100%", maxWidth: "600px", mb: 1.25 }}>
                  <SkeletonLoader count={1} styles={{ height: 400, width: "100%" }} />
                </Box>
              )}
              <img
                ref={imageRef}
                src={WorldMap}
                alt="World Map"
                onLoad={() => setImageLoaded(true)}
                style={{
                  width: "100%",
                  maxWidth: "600px",
                  marginBottom: "10px",
                  display: imageLoaded ? "block" : "none",
                }}
              />
              {!imageLoaded ? (
                <Box sx={{ width: "100%", maxWidth: "600px", mt: 2 }}>
                  <Skeleton
                    variant="text"
                    width="45%"
                    height={36}
                    sx={{ borderRadius: 1 }}
                  />
                  <Skeleton
                    variant="text"
                    width="85%"
                    height={24}
                    sx={{ borderRadius: 1 }}
                  />
                  <Skeleton
                    variant="text"
                    width="70%"
                    height={24}
                    sx={{ borderRadius: 1 }}
                  />
                </Box>
              ) : (
                <>
                  <Typography
                    variant="h5"
                    color="primary"
                    fontWeight={600}
                    gutterBottom
                    sx={{ color: "#696595" }}
                  >
                    Region Management
                  </Typography>
                  <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  marginTop: "-70px",
                }}
              >
                    <Typography
                      variant="body1"
                      maxWidth="500px"
                      color={isDarkMode ? "#fff" : "#110F27"}
                      sx={{ mr: 2 }}
                    >
                      Set up regional configurations for your organization's assets
                      to view security posture metrics by region.
                    </Typography>
                   <img
                  src={Region_UPArrow_img}
                  alt=""
                  style={{ marginBottom: "70px" }}
                />
              </div>
             </>
              )}
            </Box>
          ) : (
            <Box sx={{ flexGrow: 1, height: "calc(100vh - 110px )" }}>
              {
                <RegionManager
                  levels={levels}
                  onConfigureBack={() => toggleDrawer()}
                  preloadedRegionData={preloadedRegionData}
                  isDisabled={drawerOpen}
                />
              }
            </Box>
          )}

          <Drawer
            anchor="right"
            open={drawerOpen}
            variant="persistent"
            onClose={() => {}} 
            ModalProps={{
              disableEscapeKeyDown: true,
              onBackdropClick: (e) => e.stopPropagation(), 
            }}
            PaperProps={{
              sx: {
                width: "400px",
                height: "calc(101vh - 100px)",
                marginTop: "80px",
                marginBottom: "30px",
                marginRight: "20px",
                borderTopLeftRadius: "16px",
                borderBottomLeftRadius: "16px",
                borderTopRightRadius: "16px",
                borderBottomRightRadius: "16px",
                boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
                overflow: "hidden",
                zIndex:1
              },
            }}
          >
            <AppBar
              position="static"
              color="default"
              sx={{ boxShadow: "none" }}
            >
              <Toolbar sx={{ justifyContent: "space-between" }}>
                <Typography
                  variant="subtitle1"
                  fontWeight={700}
                  fontSize={"18px"}
                >
                  Configure Levels
                </Typography>
                {levels.length > 0 && (
                  <IconButton onClick={toggleDrawer}>
                    <Close />
                  </IconButton>
                )}
              </Toolbar>
              <Typography
                variant="body2"
                sx={{ fontWeight: "500", pl: 3, pr: 1, pb: 2 }}
                gutterBottom
              >
                Build a regional hierarchy of up to five levels to organize your
                assets effectively
              </Typography>
            </AppBar>

            <Box p={3} width={400}>
              {levels.length > 0 ? (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 3,
                    position: "relative",
                  }}
                >
                  {[...levels]
                    .sort((a, b) => a.level - b.level)
                    .map((level: any, index: number) => (
                      <Box
                        key={level.level}
                        sx={{
                          position: "relative",
                          ml: index * 3,
                          "&::before":
                            index > 0
                              ? {
                                  content: '""',
                                  position: "absolute",
                                  left: -24,
                                  top: -20,
                                  width: 2,
                                  height: 40,
                                  bgcolor: "#e0e0e0",
                                }
                              : {},
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          {index > 0 && (
                            <Box
                              sx={{
                                position: "absolute",
                                left: -24,
                                width: 24,
                                height: 2,
                                bgcolor: "#e0e0e0",
                              }}
                            />
                          )}
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              width: "100%",
                            }}
                          >
                            <Typography
                              sx={{
                                position: "absolute",
                                top: -20,
                                left: 0,

                                fontSize: "0.875rem",
                                color: "text.secondary",
                              }}
                            >
                              {index === 0 ? "Level 1" : `Level ${index + 1}`}
                            </Typography>
                            {/* <TextField
                          fullWidth
                          placeholder={index === 0 ? 'Country' : 'City'}
                          value={level.name}
                          onChange={(e) => handleChange(index, e.target.value)}
                          variant="outlined"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              bgcolor: index === 0 ? '#F8F9FC' : 'transparent',
                              borderRadius: '4px',
                            }
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              handleSingleLevelSave(level);
                            }
                          }}
                        /> */}
                            <TextField
                              fullWidth
                              placeholder={index === 0 ? "Country" : "City"}
                              value={
                                level && level.level in inputValues
                                  ? inputValues[level.level]
                                  : level?.name ?? ""
                              }
                              onChange={(e) => {
                                const filteredValue = filterLevelNameInput(e.target.value);
                                setInputValues((prev) => ({
                                  ...prev,
                                  [level.level]: filteredValue,
                                }));
                              }}
                              variant="outlined"
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  bgcolor:
index === 0 ? (isDarkMode ? "#1E1E1E" : "#F8F9FC") : "transparent",
                                  borderRadius: "4px",
                                },
                              }}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  const name = inputValues[level.level]?.trim();
                                  if (isValidLevelName(name)) {
                                    handleSingleLevelSave({ ...level, name });
                                  }
                                }
                              }}
                              InputProps={{
                                endAdornment: (
                                  <Button
                                    onClick={() => {
                                      const name =
                                        inputValues[level.level]?.trim();
                                      if (name) {
                                        handleSingleLevelSave({ ...level, name });
                                      }
                                    }}
                                    disabled={
                                      !inputValues[level.level] ||
                                      !isValidLevelName(inputValues[level.level]) ||
                                      inputValues[level.level] === savedValues[level.level] ||
                                      !hasPermissions(REGION_ROUTE.MODIFY_REGION_LEVEL)
                                    }
                                    size="small"
                                  >
                                    Save
                                  </Button>
                                ),
                              }}
                            />

                          {hasPermissions(REGION_ROUTE.MODIFY_REGION_LEVEL) && (
                             <Box sx={{ ml: 1, display: "flex", gap: 0.5 }}>
                              {index === levels.length - 1 &&
                                levels.length < 5 && (
                                  <IconButton
                                    onClick={handleAddLevel}
                                    disabled={isAnySaveEnabled}
                                    sx={{
                                      bgcolor: "#0000FF",
                                      color: "white",
                                      "&:hover": { bgcolor: "#0000DD" },
                                      width: 32,
                                      height: 32,
                                    }}
                                  >
                                    <Add fontSize="small" />
                                  </IconButton>
                                )}
                              {index === levels.length - 1 && (
                                <IconButton
                                  onClick={() => confirmDelete(level)}
                                  disabled={isAnySaveEnabled}
                                  sx={{
                                    bgcolor: "#FF4D4F",
                                    color: "white",
                                    "&:hover": { bgcolor: "#FF3B3B" },
                                    width: 32,
                                    height: 32,
                                  }}
                                >
                                  <Delete fontSize="small" />
                                </IconButton>
                              )}
                            </Box>
                          )}
                          </Box>
                        </Box>
                      </Box>
                    ))}
                </Box>
              ) : (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  mb={2}
                >
                  <Button
                    variant="outlined"
                    onClick={handleAddLevel}
                    sx={{
                      width: "100%",
                      borderColor: "primary.main",
                      color: "primary.main",
                      "&:hover": {
                        borderColor: "primary.dark",
                        bgcolor: "primary.50",
                      },
                    }}
                  >
                    Add Level
                  </Button>
                </Box>
              )}

              {/* <Button
            fullWidth
            variant="contained"
            onClick={handleSave}
            disabled={!isValid}
            sx={{ mt: 2, bgcolor: '#0f06f2', '&:hover': { bgcolor: '#0c05c2' }, '&:disabled': { bgcolor: '#e0e0e0' } }}
          >
            Save
          </Button> */}
            </Box>
          </Drawer>

          <Dialog open={openDialog} onClose={handleCloseDialog}  sx={{
            "& .MuiDialog-paper": {
              position: "absolute",
              top: "50%",
              left: "50%",
              width: "400px",
              height: "170px",
              borderRadius: "4px",
              transform: "translate(-50%, -50%)",
              // padding: "10px 10px 0",
              // padding:"3px",
              boxSizing: "border-box",
              overflow: "hidden", 
            },
          }}>
            <DialogTitle sx={{ textAlign: "center", fontWeight: 700 }}>
              Are you sure you want to delete this level?
            </DialogTitle>
            <DialogActions sx={{ justifyContent: "center", pb: 2 }}>
              <Button
                variant="outlined"
                onClick={handleCloseDialog}
                sx={{ color: "#6c63ff" ,width:130}}
              >
                No
              </Button>
              <Button
                variant="contained"
                onClick={handleConfirmDelete}
                sx={{ backgroundColor: "#ff4d4f", color: "#fff" ,width:130}}
              >
                Yes
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      )}
    </>
  );
};

export default RegionManagement;
